import type { Dictionary } from 'ramda';
import { createSelector } from 'reselect';

// eslint-disable-next-line import/named
import { LOCALES } from 'constants/dateTime';
import { formatFiat } from 'helpers/currency';
import { computeBalances, computeBalancesBySymbol } from 'helpers/formulas';
import {
  contains,
  filter,
  forEach,
  indexBy,
  isEmpty,
  map,
  mergeAll,
  path,
  pathOr,
  pipe,
  prop,
  reduce,
  sortBy,
  values,
} from 'helpers/ramda';
import {
  calculateTotalWalletBalanceInINR,
  convertFromINRtoUSD,
} from 'helpers/spotWalletHelpers';
import {
  addZeroesUntilCorrectPrecision,
  convertExponentialToDecimal,
  convertFromUSDtoINR,
  cropAfterDecimals,
  getBalanceBySymbol,
  getMarginModeAggregateStatus,
  getPercentageUpdated,
  percentageForIMAndMM,
  sanitizeValue,
} from 'helpers/utils';
import { walletStoreSelector } from 'stores/wallet/wallet.selectors';
import type { Asset, AssetBySymbol } from 'types/IAsset';
import type IState from 'types/Istore';
import IUser from 'types/IUser';
import type { IBalance } from 'types/IWallet';

import {
  CURRENCY_TYPE,
  ENABLED_WALLETS,
  TOTAL_EQUITY_DECIMAL,
  VANILLA_SETTLING_ASSET,
} from '../constants';
import { inrConversionRateSelector, userSelector } from './otherSelectors';
import {
  selectedProductState,
  spotIndicesSelector,
  spotContractTypeProductsSelector,
  withdrawalLimitsSelector,
} from './tradeSelectors';

const walletSelector = (state: IState) => state.wallet;

// export const balanceSelector = (state: IState) => state.wallet.balances;
export const rawBalanceSelector = createSelector(
  [walletSelector],
  wallet => wallet.raw_balances
);

export const rawInvestBalanceSelector = createSelector(
  [walletSelector],
  wallet => wallet.raw_invest_balances
);

export const showPopupSelector = createSelector(
  [walletSelector],
  wallet => wallet.show_popup
);
export const walletPopupTypeSelector = createSelector(
  [walletSelector],
  wallet => wallet.popup_type
);

export const lienAmountSelector = createSelector(
  [userSelector, rawBalanceSelector],
  (user: IUser, rawBalances = []) => {
    const lienAmount =
      rawBalances.filter(balance => balance?.asset_symbol === CURRENCY_TYPE.USD)?.[0]
        ?.lien_amount || '0';

    const isLienApplied =
      Boolean(Number(lienAmount)) || Boolean(user?.permissions?.is_lien_applied);

    return {
      lienAmount,
      isLienApplied,
    };
  }
);

export const rawBalanceAssetIdSelector = createSelector(
  [rawBalanceSelector],
  balances => balances?.[0]?.asset_id
);

export const portfolioMarginSelector = () => walletStoreSelector().portfolio_margin;
export const multiCollateralSelector = () => walletStoreSelector().multiCollateral;
export const availableMarginCrossSelector = () =>
  walletStoreSelector().available_margin_cross;
export const crossMarginChannelSelector = () => walletStoreSelector().crossMarginChannel;

export const addressSelector = createSelector([walletSelector], wallet => wallet.address);
export const roboTradingEquitySelector = createSelector(
  [walletSelector],
  wallet => wallet.robo_trading_equity
);

export const pendingDepositsSelector = (state: IState) => state.wallet.pendingDeposits;

export const fiatDepositsSelector = (state: IState) => state.wallet.fiatDeposits;

export const conversionRateSelector = (state: IState) => state.wallet.conversionRate;
export const isConvertingSelector = (state: IState) => state.wallet.isConverting;
export const isCurrencyPopupOpenSelector = createSelector(
  [walletSelector],
  wallet => wallet.is_currency_popup_open
);
export const convertedDataSelector = (state: IState) => state.wallet.convertedData;
export const conversionConfigSelector = (state: IState) => state.wallet.conversionConfig;
export const coinSelectedSelector = createSelector(
  [walletSelector],
  wallet => wallet.coin_selected
);

const assetSelector = (state: IState) => state.trade.assets;
// const selectedProductSettlingAsset = (state: IState) =>
//   state.trade.selected_product.settling_asset;

export const yieldInvestStrategiesSelector = createSelector(
  [rawInvestBalanceSelector],
  balances =>
    balances && filter(balance => balance.fund_type === 'covered_options', balances)
);

export const stakingInvestStrategiesSelector = createSelector(
  [rawInvestBalanceSelector],
  balances =>
    balances && filter(balance => balance.fund_type === 'staking_pool', balances)
);

export const roboInvestStrategiesSelector = createSelector(
  [rawInvestBalanceSelector],
  balances =>
    balances &&
    filter(
      balance =>
        balance.fund_type !== 'covered_options' && balance.fund_type !== 'staking_pool',
      balances
    )
);

// @ts-ignore
export const assetBySymbolSelector: AssetBySymbol = createSelector(
  [assetSelector],
  assets => indexBy(prop('symbol'), values(assets))
);

const balancesWithAssets = createSelector(
  [assetSelector, rawBalanceSelector],
  (assets, balances) => {
    if (!isEmpty(assets) && !isEmpty(balances)) {
      const balanceWithAsset = forEach(
        // eslint-disable-next-line no-return-assign, no-param-reassign
        (obj: any) => (obj.asset = assets[obj.asset_id]),
        balances
      );
      return balanceWithAsset;
    }
    return [];
  }
);

export const balanceSelector = createSelector([balancesWithAssets], balances => {
  return computeBalances(balances);
});

export const balanceBySymbolSelector = createSelector([balancesWithAssets], balances => {
  return computeBalancesBySymbol(balances);
});

export const usdAssetMinPrecisionSelector = createSelector(
  [balanceBySymbolSelector],
  balances => {
    const usdBalance = prop(VANILLA_SETTLING_ASSET as string, balances) || {};
    return usdBalance?.asset?.minimum_precision ?? 2;
  }
);

export const balanceState = createSelector(balanceSelector, balances => {
  return filter(balance => contains(balance?.asset.symbol, ENABLED_WALLETS), balances);
});

export const enableWalletsSelector = createSelector(
  balanceBySymbolSelector,
  (balances: Dictionary<IBalance>) => {
    return filter<IBalance, IBalance>(balance =>
      contains(balance.asset.symbol, ENABLED_WALLETS)
    )(balances);
  }
);

export const withDrawalEnabledWalletsSelectors = createSelector(
  balanceBySymbolSelector,
  balances => {
    const isWithdrawalEnabled = balance =>
      contains(balance.asset.symbol, ENABLED_WALLETS);
    return filter(isWithdrawalEnabled, balances);
  }
);

export const checkEnabledWalletsTotalBalanceSelector = createSelector(
  enableWalletsSelector,
  (balance: Dictionary<IBalance>) => {
    return Object.values(balance)
      .map(b => b.balance)
      .reduce(
        (accumulator, currentValue) => Number(accumulator) + Number(currentValue),
        0
      );
  }
);

const depositEnabledNetworksSelectors = createSelector(
  assetSelector,
  pipe(
    values,
    reduce<Asset, Record<string, Asset['networks']>>((accumulated, currentAsset) => {
      const { symbol, networks } = currentAsset;

      /* Filter out the disabled assets */
      if (!contains(symbol, ENABLED_WALLETS)) {
        return accumulated;
      }

      /* Filter out the disabled networks from the current asset */
      const depositEnabledNetworks = filter(
        network => network.deposit_status === 'enabled',
        networks
      );

      return {
        ...accumulated,
        [symbol]: depositEnabledNetworks,
      };
    }, {})
  )
);

// Returns wallet ids [2,13]
export const walletIdListSelector = createSelector(balanceState, balanceObj => {
  const wallets = values(balanceObj);
  return wallets.map(path(['asset', 'id']));
});

//  returns asset ids with symbol
export const assetSymbolIdSelector = createSelector(balanceState, balanceObj => {
  const wallets = values(balanceObj);

  return mergeAll(map(wallet => ({ [wallet.asset.symbol]: wallet.asset.id }), wallets));
});

export const totalBalanceState = createSelector(
  [balanceSelector, selectedProductState, assetSymbolIdSelector],
  (balances, selectedProduct, assetIDBySymbol) => {
    if (selectedProduct) {
      const { contract_type: contractType } = selectedProduct;
      const id =
        contractType === 'spot'
          ? selectedProduct.underlying_asset.id
          : selectedProduct.settling_asset.id;
      const balance = pathOr('-', [id, 'balance'], balances);
      return `${balance}`;
    }
    const id = assetIDBySymbol.BTC;
    const balance = pathOr('-', [id, 'balance'], balances);
    return `${balance}`;
  }
);

export const availableBalanceState = createSelector(
  [balanceSelector, selectedProductState, assetSymbolIdSelector],
  (balances, selectedProduct, assetIDBySymbol) => {
    if (selectedProduct) {
      const { contract_type: contractType } = selectedProduct;
      const id =
        contractType === 'spot'
          ? selectedProduct.underlying_asset.id
          : selectedProduct.settling_asset.id;
      const balance = pathOr('-', [id, 'available_balance'], balances);
      return `${balance}`;
    }
    const id = assetIDBySymbol.BTC;
    const balance = pathOr('-', [id, 'available_balance'], balances);
    return `${balance}`;
  }
);

export const deltaCashSelector = createSelector([balanceBySymbolSelector], balances => {
  const feeCreditsUSDT = balances?.USDT?.trading_fee_credit_precise;
  return feeCreditsUSDT;
});

const deltaCashSelectorUsd = createSelector([balanceBySymbolSelector], balances => {
  const feeCreditsUSD = balances?.USD?.trading_fee_credit_precise;
  return feeCreditsUSD;
});

/**
 * Select available_balance from USD balances, this asset is available for Indian exchange.
 */
const selectUsdAssetAvailableBalance = createSelector(
  [balanceBySymbolSelector],
  balances => {
    return balances?.USD?.available_balance ?? null;
  }
);
const sortedEnabledWalletSelectors = createSelector([enableWalletsSelector], balances => {
  return sortBy(({ asset }) => Number(asset?.sort_priority), values(balances));
});

const blockedMarginSelectorUsd = createSelector([balanceBySymbolSelector], balances => {
  const blockedMargin = balances?.USD?.blocked_margin;
  return blockedMargin;
});

/**
 @deprecated use from useWalletStoreBase in wallet.store.ts
*/
const blockedMarginCrossModeSelectorUsd = createSelector(
  [multiCollateralSelector],
  multiCollateral => {
    const blockedMarginCrossMode = multiCollateral?.wallets.reduce((acc, wallet) => {
      return (
        acc +
        parseFloat(String(wallet?.cross_position_margin ?? 0)) +
        parseFloat(String(wallet?.cross_order_margin ?? 0)) +
        parseFloat(String(wallet?.cross_commission ?? 0))
      );
    }, 0);
    return blockedMarginCrossMode;
  }
);

const marginModeAggregateStatusPortfolioModeSelector = createSelector(
  [portfolioMarginSelector, balanceBySymbolSelector],
  (portfolioMargin, allBalances) => {
    const { balance } = getBalanceBySymbol(CURRENCY_TYPE.USD, allBalances) || {};

    const aggregatePortfolioData = portfolioMargin.aggregate || {};
    const {
      liquidation_risk: liquidationRisk,
      under_liquidation: underLiquidation,
      positions_upl: positionsUpl,
      im_wo_ucf: initialMarginWithoutUcf,
    } = aggregatePortfolioData;

    const initialMarginPercentage = percentageForIMAndMM(
      sanitizeValue(initialMarginWithoutUcf),
      sanitizeValue(balance),
      sanitizeValue(positionsUpl)
    );

    const aggregateStatusPortfolioMode = getMarginModeAggregateStatus({
      liquidationRisk,
      underLiquidation,
      initialMarginPercentage,
    });

    return aggregateStatusPortfolioMode;
  }
);

/**
 @deprecated use from useWalletStoreBase in wallet.store.ts
*/
const marginModeAggregateStatusCrossModeSelector = createSelector(
  [crossMarginChannelSelector],
  crossMarginData => {
    const {
      im_margins: initialMarginPercentage,
      liquidation_risk: liquidationRisk,
      under_liquidation: underLiquidation,
    } = crossMarginData ?? {};

    const aggregateStatusCrossMode = getMarginModeAggregateStatus({
      liquidationRisk,
      underLiquidation,
      initialMarginPercentage,
    });

    return aggregateStatusCrossMode;
  }
);

const blockedMarginPortfolioModeSelector = createSelector(
  [portfolioMarginSelector, balanceBySymbolSelector],
  (portfolioMargin, allBalances) => {
    const aggregatePortfolioData = portfolioMargin.aggregate || {};
    const { positions_upl: positionsUpl, blocked_margin: blockedMarginRaw } =
      aggregatePortfolioData;
    const { balance } = getBalanceBySymbol(CURRENCY_TYPE.USD, allBalances) || {};

    const blockedMarginSanitizedValue = sanitizeValue(blockedMarginRaw);
    const positionsUplSanitizedValue = sanitizeValue(positionsUpl);

    const blockedMarginWithUpl =
      Number(blockedMarginSanitizedValue) + Number(positionsUplSanitizedValue);

    const blockedMarginPercentage = getPercentageUpdated(
      blockedMarginWithUpl,
      Number(sanitizeValue(balance)) + Number(positionsUplSanitizedValue),
      2
    );

    return {
      blockedMargin: blockedMarginWithUpl,
      blockedMarginPercentage,
    };
  }
);

const fundsAddedSelector = createSelector([enableWalletsSelector], balances => {
  return Boolean(Number(balances?.USD?.balance ?? 0));
});

const inrBalanceSelector = createSelector(
  [enableWalletsSelector, inrConversionRateSelector, withdrawalLimitsSelector],
  (balances, inrConversionRate, withdrawalLimits) => {
    const usdBalanceObject = balances?.USD ?? {};
    const usdAssetMinPrecision = usdBalanceObject?.asset?.minimum_precision ?? 2;

    const rawInrBalance = convertFromUSDtoINR(
      Number(withdrawalLimits?.max_withdrawal_after_forfeiting),
      inrConversionRate
    );

    const formattedInrBalance = cropAfterDecimals(
      addZeroesUntilCorrectPrecision(rawInrBalance, usdAssetMinPrecision),
      usdAssetMinPrecision
    );
    return { formattedInrBalance, rawInrBalance };
  }
);

const accountBalanceSelector = createSelector(
  [enableWalletsSelector, balanceBySymbolSelector],
  (balances, allBalances) => {
    const { balance, balance_inr: balanceInr } = getBalanceBySymbol(
      VANILLA_SETTLING_ASSET,
      allBalances
    ) ?? {
      balance: 0,
      balance_inr: 0,
    };

    const usdBalanceObject = balances?.USD ?? {};

    const usdAssetMinPrecision = usdBalanceObject?.asset?.minimum_precision ?? 2;

    const accountBalanceFormattedInUSD = formatFiat(
      CURRENCY_TYPE.USD,
      convertExponentialToDecimal(
        cropAfterDecimals(
          addZeroesUntilCorrectPrecision(Number(balance), usdAssetMinPrecision),
          usdAssetMinPrecision
        )
      ),
      LOCALES.GLOBAL,
      {
        maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
      }
    );

    const accountBalanceFormattedInINR = formatFiat(
      CURRENCY_TYPE.INR,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(Number(balanceInr), usdAssetMinPrecision),
        usdAssetMinPrecision
      ),
      LOCALES.INDIA,
      {
        maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
      }
    );

    return {
      accountBalanceFormattedInUSD,
      accountBalanceFormattedInINR,
    };
  }
);

const spotWalletBalanceList = createSelector(
  [walletSelector],
  walletState => walletState?.spot_wallet_balance_list
);

const onlyINRWalletBalance = createSelector(
  [walletSelector],
  walletState =>
    walletState?.spot_wallet_balance_list.find(
      wallet => wallet.asset_symbol === CURRENCY_TYPE.INR
    )?.balance
);

export const spotIndiaSymbolsSelector = createSelector(
  [spotContractTypeProductsSelector, spotIndicesSelector],
  (spotProducts, spotIndices) => {
    if (!spotProducts || spotProducts.length === 0 || !spotIndices) {
      return {};
    }

    const symbolsMap: Record<string, string> = {};
    spotProducts.forEach(
      (product: {
        id: string;
        underlying_asset_id: number;
        quoting_asset?: { id: number; symbol: string };
      }) => {
        // Only process INR-quoted products
        if (product.quoting_asset?.symbol !== 'INR') {
          return;
        }

        // Find the INR-quoted spot index for this underlying asset
        const inrSpotIndex = Object.values(spotIndices).find(
          (index: any) =>
            index.underlying_asset_id === product.underlying_asset_id &&
            index.quoting_asset_id === product.quoting_asset?.id
        );

        if (inrSpotIndex?.symbol) {
          symbolsMap[product?.id ?? 'NA'] = inrSpotIndex.symbol;
        }
      }
    );

    return symbolsMap;
  }
);

export const spotIndiaSymbolsSelectorWithBalance = createSelector(
  [spotContractTypeProductsSelector, spotIndicesSelector, spotWalletBalanceList],
  (spotProducts, spotIndices, _spotWalletBalanceList) => {
    if (!spotProducts || spotProducts.length === 0 || !spotIndices) {
      return {};
    }

    const walletWithBalance = _spotWalletBalanceList.filter(wallet => {
      const balance =
        typeof wallet.balance === 'string' ? parseFloat(wallet.balance) : wallet.balance;
      return balance > 0 && wallet.asset_symbol !== 'INR';
    });
    const symbolsMap: Record<string, string> = {};
    spotProducts.forEach(
      (product: {
        id: string;
        underlying_asset_id: number;
        quoting_asset?: { id: number; symbol: string };
      }) => {
        // Only process INR-quoted products
        if (product.quoting_asset?.symbol !== 'INR') {
          return;
        }

        if (
          !walletWithBalance?.find(
            wallet => wallet.asset_id === product.underlying_asset_id
          )
        ) {
          return;
        }

        // Find the INR-quoted spot index for this underlying asset
        const inrSpotIndex = Object.values(spotIndices).find(
          (index: any) =>
            index.underlying_asset_id === product.underlying_asset_id &&
            index.quoting_asset_id === product.quoting_asset?.id
        );

        if (inrSpotIndex?.symbol) {
          symbolsMap[product?.id ?? 'NA'] = inrSpotIndex.symbol;
        }
      }
    );

    return symbolsMap;
  }
);

export const spotIndiaTickerSelector = createSelector(
  [spotContractTypeProductsSelector],
  spotProducts => {
    if (!spotProducts || spotProducts.length === 0) {
      return {};
    }

    const symbolsMap: Record<string, string> = {};

    // just add symbol from spotProducts, dont need spotIndices
    spotProducts.forEach((product: { id: string; symbol: string }) => {
      if (product.symbol) {
        symbolsMap[product?.id ?? 'NA'] = product.symbol;
      }
    });

    return symbolsMap;
  }
);

/**
 * Selector to calculate total spot wallet balance in INR
 * @param spotPrices - Spot prices from Zustand store (passed from component)
/**
 * Selector to calculate total spot wallet balance in INR
 * @param spotPrices - Spot prices from Zustand store (passed from component)
 * @returns Total spot wallet balance in INR
 */
const totalSpotWalletBalanceInINR = createSelector(
  [
    spotWalletBalanceList,
    (_state: IState, spotPrices: Record<string, number | undefined>) => spotPrices,
  ],
  (walletList, spotPrices) => {
    return calculateTotalWalletBalanceInINR(walletList, spotPrices);
  }
);

/**
 * Selector to calculate total spot wallet balance in USD
 * @param spotPrices - Spot prices from Zustand store (passed from component)
 * @returns Total spot wallet balance in USD
 */
const totalSpotWalletBalanceInUSD = createSelector(
  [
    (_state: IState, spotPrices: Record<string, number | undefined>) => {
      // Calculate INR balance first
      const walletList = spotWalletBalanceList(_state);
      return calculateTotalWalletBalanceInINR(walletList, spotPrices);
    },
    inrConversionRateSelector,
  ],
  (balanceInINR, inrConversionRate) => {
    return convertFromINRtoUSD(balanceInINR, inrConversionRate);
  }
);

export {
  deltaCashSelectorUsd,
  depositEnabledNetworksSelectors,
  selectUsdAssetAvailableBalance,
  sortedEnabledWalletSelectors,
  blockedMarginSelectorUsd,
  blockedMarginCrossModeSelectorUsd,
  marginModeAggregateStatusPortfolioModeSelector,
  marginModeAggregateStatusCrossModeSelector,
  blockedMarginPortfolioModeSelector,
  fundsAddedSelector,
  inrBalanceSelector,
  accountBalanceSelector,
  spotWalletBalanceList,
  totalSpotWalletBalanceInINR,
  totalSpotWalletBalanceInUSD,
  onlyINRWalletBalance,
};
